/**
 * Runtime environment variable helper
 *
 * Provides a unified interface for accessing environment variables that works
 * in both Vite (browser) and Jest (Node.js) test environments.
 *
 * In Vite: vite.config.ts injects __VITE_API_URL__ and __VITE_ENV__ via the
 *   `define` option. Vite replaces these identifiers at build/dev time with the
 *   actual string values from process.env / .env files.
 *   Using `define` instead of `import.meta.env.*` means this file contains no
 *   `import.meta` syntax and can be safely parsed by Jest's CJS runtime.
 *
 * In Jest: reads from `process.env` (which Jest populates from .env files).
 */

// __VITE_API_URL__ and __VITE_ENV__ are replaced at build time by Vite's
// `define` config.  In Jest/Node they are undeclared; `typeof` on an undeclared
// identifier always returns 'undefined' without throwing, so this is safe.

const _VITE_API_URL: string =
   
  typeof __VITE_API_URL__ !== 'undefined' ? __VITE_API_URL__ : '';
const _VITE_ENV: string =
   
  typeof __VITE_ENV__ !== 'undefined' ? __VITE_ENV__ : '';

// Keyed map for generic lookup
const VITE_SNAPSHOT: Record<string, string> = {
  VITE_API_URL: _VITE_API_URL,
  VITE_ENV: _VITE_ENV,
};

function getRuntimeEnv(key: string, defaultValue: string = ''): string {
  // Node.js / Jest environment
  if (typeof process !== 'undefined' && process.env && typeof window === 'undefined') {
    return process.env[key] || defaultValue;
  }

  // Browser / Vite production build — values were inlined by Vite above
  return VITE_SNAPSHOT[key] || defaultValue;
}

export const runtimeEnv = {
  /**
   * Get the API base URL for backend requests.
   * Defaults to empty string for relative paths (proxy via frontend server).
   */
  getApiUrl(): string {
    return getRuntimeEnv('VITE_API_URL', '');
  },

  /**
   * Absolute API origin for navigations that must land on the API host itself
   * rather than the same-origin /api proxy: OAuth, whose callback URLs are
   * registered with each provider on the API host. Empty in a dev build, where
   * each caller knows its local address.
   */
  getApiOrigin(): string {
    const devBuild = typeof __VITE_DEV__ === 'undefined' || __VITE_DEV__;
    return getRuntimeEnv('VITE_API_URL', '') || (devBuild ? '' : 'https://api.adrodd.co.uk');
  },

  /**
   * Get a raw environment variable by key.
   */
  get(key: string, defaultValue?: string): string {
    return getRuntimeEnv(key, defaultValue ?? '');
  },
};

export default runtimeEnv;
